import { FormEvent, useEffect, useRef, useState } from 'react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import Link from 'next/link';

import { contactFormRequest } from '@/apis/requests/contact.requests';
import { CONTACT_US_TYPES } from '@/types/entities/contact.entities';

const SUBSCRIBE_CONSENT =
  'Email me when the next SA Cyber and Chat.co webinar is announced. I can unsubscribe at any time.';

const inputClass =
  'mt-2 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-3 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#1c5791] focus:ring-2 focus:ring-blue-100';

/**
 * Standing opt-in for the series. Unlike RegistrationForm this is not tied to
 * one event — it asks for the minimum needed to send the next invitation.
 */
export default function SubscribeForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [error, setError] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const turnstile = useRef<TurnstileInstance>(null);
  const inFlight = useRef(false);
  const result = useRef<HTMLDivElement>(null);
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (status === 'success' || error) result.current?.focus();
  }, [status, error]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (inFlight.current) return;
    setError('');
    const data = new FormData(event.currentTarget);
    const value = (key: string) => String(data.get(key) || '').trim();
    if (value('name').length < 2) {
      setError('Please enter your name.');
      return;
    }
    if (siteKey && !token) {
      setError('Please complete the verification check before subscribing.');
      return;
    }
    inFlight.current = true;
    setStatus('submitting');
    try {
      await contactFormRequest({
        name: value('name'),
        email: value('email').toLowerCase(),
        companyName: value('company') || undefined,
        type: CONTACT_US_TYPES.CONTACT,
        subject: 'Webinar series subscription: Chat.co Security Series',
        message: [
          'Request: Notify me about upcoming Chat.co Security Series webinars',
          `Company: ${value('company') || 'Not provided'}`,
          `Opt-in wording: ${SUBSCRIBE_CONSENT}`,
          'Consent notice version: 2026-09-09',
          'Source: /webinars',
        ].join('\n'),
        ...(token ? { turnstileToken: token } : {}),
      });
      setStatus('success');
    } catch {
      setStatus('idle');
      setError(
        'We couldn’t save your details. Please try again. If this continues, contact hello@chat.co.'
      );
      setToken(null);
      turnstile.current?.reset();
    } finally {
      inFlight.current = false;
    }
  }

  if (status === 'success')
    return (
      <div
        ref={result}
        tabIndex={-1}
        role="status"
        className="rounded-[28px] border border-emerald-200 bg-white p-8 shadow-xl outline-none sm:p-10">
        <div
          aria-hidden="true"
          className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-3xl text-emerald-700">
          ✓
        </div>
        <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">
          You’re on the list
        </p>
        <h2 className="mt-3 text-2xl font-bold text-[#06264b] sm:text-3xl">
          We’ll email you when session 2 is scheduled.
        </h2>
        <p className="mt-4 leading-relaxed text-slate-600">
          In the meantime, the full replay of session 1 is right here — no sign-up needed.
        </p>
      </div>
    );

  return (
    <form
      onSubmit={submit}
      className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-xl shadow-slate-900/5 sm:p-8"
      aria-labelledby="subscribe-title">
      <div className="mb-6 border-b border-slate-100 pb-6">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#866112]">
          Never miss a session
        </p>
        <h2
          id="subscribe-title"
          className="mt-2 text-2xl font-bold tracking-tight text-[#06264b] sm:text-3xl">
          Get the next webinar in your inbox.
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          One short email per session. No newsletter, no sales sequence.
        </p>
      </div>
      <fieldset disabled={status === 'submitting'} className="space-y-4">
        <legend className="sr-only">Subscriber details</legend>
        <label className="block text-sm font-semibold text-slate-700" htmlFor="subscribe-name">
          Full name <span aria-hidden="true">*</span>
          <input
            id="subscribe-name"
            name="name"
            autoComplete="name"
            required
            minLength={2}
            maxLength={100}
            placeholder="Alex Morgan"
            className={inputClass}
          />
        </label>
        <label className="block text-sm font-semibold text-slate-700" htmlFor="subscribe-email">
          Work email <span aria-hidden="true">*</span>
          <input
            id="subscribe-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            maxLength={255}
            placeholder="alex@company.com"
            className={inputClass}
          />
        </label>
        <label className="block text-sm font-semibold text-slate-700" htmlFor="subscribe-company">
          Company <span className="font-normal text-slate-500">(optional)</span>
          <input
            id="subscribe-company"
            name="company"
            autoComplete="organization"
            maxLength={100}
            placeholder="Company name"
            className={inputClass}
          />
        </label>
        <p className="text-xs leading-5 text-slate-500">
          {SUBSCRIBE_CONSENT} Read our{' '}
          <Link href="/privacy-policy" className="text-[#15558e] underline underline-offset-2">
            Privacy Policy
          </Link>
          .
        </p>
        {siteKey && (
          <Turnstile
            ref={turnstile}
            siteKey={siteKey}
            onSuccess={setToken}
            onExpire={() => setToken(null)}
            onError={() => {
              setToken(null);
              setError('Verification could not load. Please refresh the page and try again.');
            }}
            options={{ theme: 'light', size: 'flexible' }}
          />
        )}
        {error && (
          <div
            ref={result}
            tabIndex={-1}
            role="alert"
            className="rounded-lg bg-red-50 p-3 text-sm text-red-800 outline-none">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={status === 'submitting' || (!!siteKey && !token)}
          className="flex w-full items-center justify-center gap-6 rounded-lg bg-[#06264b] px-6 py-4 font-semibold text-white transition hover:bg-[#124474] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#15558e] disabled:cursor-not-allowed disabled:opacity-60">
          {status === 'submitting' ? 'Saving your details…' : 'Notify me about the next session'}
          <span aria-hidden="true">→</span>
        </button>
        <p className="text-center text-xs text-slate-500">
          Free to attend · No Chat.co account needed
        </p>
      </fieldset>
    </form>
  );
}
